import { Stack, Table, TableBody, TableCell, TableContainer, TableHead, Tooltip } from "@mui/material";
import { Props } from "./Props";
import { PortfolioItemActions } from "../../../../shared/components/portfolio-item-actions/PortfolioItemActions";
import { AssetType } from "../../../../shared/types/AssetType";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { TableBodyRow, TableHeaderRow } from "../../../../screens/shared/views";
import { OPTIONS_TABLE_ROW_PADDING_Y, } from "../../../../screens/shared/constants/table.constants";
import { numberFormatters } from "../../../../screens/shared/utils/numberFormatters";
import { ApplicationTooltip } from "../../../../screens/positions-and-orders/views";

export function OptionsTable({
    marketDataDto,
    optionInfoData,
    selectedExpiration,
    addOptionToPortfolio
} : Props) {
    const tablePadding: number = 5;
    const currentPrice = marketDataDto!.UnderlyingPrice!;

    const getAtmStrike = (): number | null => {
        const currentPrice = marketDataDto!.UnderlyingPrice!;
        const strikes = optionInfoData
            .filter(x => dayjs(selectedExpiration).isSame(dayjs(x.ExpireAt), 'day'))
            .map(x => x.Strike);

        for (let i = 0; i < strikes.length - 1; i++) {
            if (strikes[i] <= currentPrice && strikes[i + 1] > currentPrice ||
                strikes[i] >= currentPrice && strikes[i + 1] < currentPrice)
            {
                return currentPrice - strikes[i] <= (strikes[i + 1] - strikes[i]) / 2
                    ? strikes[i]
                    : strikes[i + 1];
            }
        }

        return null;
    }
    
    const [atmStrike, setAtmStrike] = useState<number | null>(getAtmStrike());
    
    useEffect(() => {
        setAtmStrike(getAtmStrike());
    }, [selectedExpiration]);
    
    return (
        <Stack
            sx={{
                flex: '1',
                overflow: 'auto'
            }}
            direction="column"
            spacing={0}
        >
            <TableContainer>
                <Table sx={{ width: 'auto' }} size="small" aria-label="caption table">
                    <TableHead>
                        <TableHeaderRow>
                            <TableCell>Actions</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Strike</TableCell>
                            <TableCell>IV,%</TableCell>
                            <ApplicationTooltip title="Risk free interest value from data provider">
                                <TableCell>R,%</TableCell>
                            </ApplicationTooltip>
                            <ApplicationTooltip title="Data provider price">
                                <TableCell>Mark price</TableCell>
                            </ApplicationTooltip>
                            <ApplicationTooltip title="Internal calculated price">
                                <TableCell>Price</TableCell>
                            </ApplicationTooltip>
                            <ApplicationTooltip title="Intrinsic value">
                                <TableCell>Int value</TableCell>
                            </ApplicationTooltip>
                            <TableCell>Delta</TableCell>
                            <TableCell>Gamma</TableCell>
                            <TableCell>Vega</TableCell>
                            <TableCell>Theta</TableCell>
                        </TableHeaderRow>
                    </TableHead>
                    <TableBody>
                        {optionInfoData
                            .filter(row => dayjs(selectedExpiration).isSame(dayjs(row.ExpireAt), 'day'))
                            .map((row) => (
                                <TableBodyRow
                                    key={row.Symbol}
                                    paddingY={OPTIONS_TABLE_ROW_PADDING_Y}
                                    sx={{
                                        ...(row.Strike === atmStrike
                                            ? {backgroundColor: 'rgba(253,197,0,0.8)'}
                                            : {}),
                                    }}
                                >
                                    <TableCell >
                                        <PortfolioItemActions
                                            action={(value: number) => addOptionToPortfolio(row, value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {
                                            row.Type == AssetType.Call
                                                ? "Call"
                                                : "Put"
                                        }
                                    </TableCell>
                                    <Tooltip
                                        title={row.Symbol}
                                    >
                                        <TableCell align="right">
                                            {numberFormatters.format0(row.Strike)}
                                        </TableCell>
                                    </Tooltip>
                                    <TableCell align="right">
                                        {row.MarkImpliedVolatility !== null
                                            ? numberFormatters.format2(row.MarkImpliedVolatility * 100)
                                            : ''
                                        }
                                    </TableCell>
                                    <TableCell align="right">
                                            {row.RiskFreeInterest !== null
                                                ? numberFormatters.format2(row.RiskFreeInterest * 100)
                                                : ''
                                            }
                                    </TableCell>
                                    <TableCell align="right">
                                        {numberFormatters.format2(row.ProviderMarkPrice)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {numberFormatters.format2(row.MarkPrice)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {
                                            numberFormatters.format2(
                                                row.Type == AssetType.Call
                                                    ? currentPrice < row.Strike
                                                        ? row.MarkPrice
                                                        : row.MarkPrice - (currentPrice - row.Strike)
                                                    : currentPrice > row.Strike
                                                        ? row.MarkPrice
                                                        : row.MarkPrice - (row.Strike - currentPrice))
                                        }
                                    </TableCell>
                                    <TableCell align="right">
                                        {numberFormatters.format2(row.Delta)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {numberFormatters.format6(row.Gamma)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {numberFormatters.format2(row.Vega)}
                                    </TableCell>
                                    <TableCell align="right">
                                        {numberFormatters.format2(row.Theta)}
                                    </TableCell>
                                </TableBodyRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    )
}